import { useState, useEffect, useRef, useCallback } from 'react';
import { SIMULATION_CONSTANTS } from '../constants';
import { speakSpeechSynthesis, stopAllSpeech } from '../../../services/voiceService';
import { simulationService } from '../services/simulation.service';

// Mic energy (0-100 RMS scale produced by the level meter) that counts as the learner
// talking while the persona still holds the floor. Kept above the room-noise floor so a
// browser-TTS bleed or keyboard noise cannot invent an interruption.
const OVERLAP_LEVEL_THRESHOLD = 18;

export interface PersonaVoiceConfig {
  personaName?: string;
  ttsVoice?: string;
  ttsProvider?: 'openai' | 'browser' | 'auto' | 'google';
  country?: string;
  tone?: string;
  pitch?: number;
  rate?: number;
  genderPreference?: string;
}

/** A learner cut-in event: the persona was still talking when the learner spoke. */
export interface InterruptionEvent {
  /** Epoch ms at which the learner started talking over the persona. */
  occurredAt: number;
  /** How long (ms) the persona had already been speaking when interrupted. */
  durationMs: number;
  /** Epoch ms the persona turn started at. */
  aiSpeechStartedAt: number;
  interruptedSpeaker: string;
  interruptedRole: 'PERSONA';
  learnerRole: 'LEARNER';
  /** 'barge_in' = explicit Spacebar / Barge In control, 'speech_overlap' = detected from mic energy. */
  source: 'barge_in' | 'speech_overlap';
}

/** Measured speech metadata attached to every finalized learner turn. */
export interface LearnerTurnSpeechMeta {
  speechStartMs?: number;
  speechEndMs?: number;
  /** Real microphone-speaking duration (first STT result → last STT result). */
  durationMs?: number;
  /** Silence (ms) between the persona finishing and the learner starting to speak. */
  hesitationMs?: number;
  wordCount?: number;
  sttConfidence?: number;
  interruption?: InterruptionEvent | null;
  interruptions?: InterruptionEvent[];
  /**
   * Resolves to the learner's recorded utterance as an audio data URL, or null when
   * the device could not record it. Sent with the turn so the results page can replay
   * the learner's real voice instead of synthesizing one.
   */
  audioPromise?: Promise<string | null>;
}

export interface UseSimulationAudioOptions {
  language?: string;
  personaName?: string;
  personaVoiceConfig?: PersonaVoiceConfig;
  onTranscript?: (text: string, meta?: LearnerTurnSpeechMeta) => void;
  onInterruption?: (event: InterruptionEvent) => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
}

/**
 * Resilient microphone acquisition helper:
 * Tries high-quality speech processing constraints first; if overconstrained or unsupported on
 * mobile Safari / Android WebViews, automatically falls back to basic audio: true.
 */
async function acquireMicrophoneStream(): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    throw new Error('getUserMedia is not supported in this browser environment');
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
  } catch (err: any) {
    if (err?.name === 'OverconstrainedError' || err?.name === 'TypeError' || err?.name === 'NotReadableError') {
      console.warn('[Trainova Audio] Complex audio constraints failed, retrying with basic { audio: true }');
      return await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Learner utterance capture — the results page must be able to replay the
// learner's own voice. Recording is scoped to the listening window (never the
// persona's audio), size- and duration-capped, and skipped silently when the
// device cannot record — mobile hands the microphone to speech recognition,
// which owns the hardware, so there is no stream to tap there.
// ---------------------------------------------------------------------------
const LEARNER_AUDIO_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'];
const MAX_LEARNER_AUDIO_BYTES = 900 * 1024;
const MAX_LEARNER_AUDIO_MS = 60_000;

function pickLearnerAudioMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of LEARNER_AUDIO_MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {}
  }
  return null;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Leading hesitation words pattern: English ("umm", "um", "hmm", "hmmm", "uh", "uhh", "ah", "er", "mmm")
 * and Hindi ("उम", "हम्म", "मम्म", "अह", "आह").
 * Strictly does NOT match confirmation words ("haan", "yes", "sure", etc.).
 */
export const LEADING_HESITATION_REGEX = /^(?:(?:\b(?:umm?|uh+m*|h+m+|a+h+|er?m?|m{2,})\b|उ+[मम्]+|ह+[मम्]+|म+[मम्]+|अ+ह+|आ+ह+)[,\s.-]*)+/i;

/**
 * Merges two transcript strings (e.g. accumulated final speech and incoming interim/chunk),
 * removing repeated words or overlapping phrases at the boundary while preserving natural
 * leading and conversational hesitation words ("umm", "um", "hmm", "uh", "uhh").
 */
export function mergeTranscriptStrings(baseText: string, incomingText: string): string {
  const base = (baseText || '').trim();
  const incoming = (incomingText || '').trim();

  if (!base) return incoming;
  if (!incoming) return base;

  // Exact match
  if (base.toLowerCase() === incoming.toLowerCase()) {
    return base;
  }

  // Incoming starts with base (hypothesis expansion: "Hello" -> "Hello world")
  if (incoming.toLowerCase().startsWith(base.toLowerCase())) {
    return incoming;
  }

  const baseWords = base.split(/\s+/);
  const incomingWords = incoming.split(/\s+/);

  // If base was purely an initial hesitation and incoming starts with an updated hesitation hypothesis
  const HESITATION_WORD_PATTERN = /^(umm|um|hmm|hmmm|uhh|uh|ah|er|mmm|उम|हम्म|मम्म|अह|आह)[.,\s-]*$/i;
  const isBaseOnlyHesitation = HESITATION_WORD_PATTERN.test(base);
  const incomingFirstWord = incomingWords[0] ? incomingWords[0].toLowerCase().replace(/[^\w]/g, '') : '';
  const incomingStartsWithHesitation = HESITATION_WORD_PATTERN.test(incomingFirstWord);
  if (isBaseOnlyHesitation && incomingStartsWithHesitation) {
    return incoming;
  }

  // Helper to normalize word for comparison (strip punctuation, lower case)
  const norm = (w: string) => w.toLowerCase().replace(/[^\w\s]/g, '');

  // Look for the longest suffix of baseWords that matches a prefix of incomingWords
  const maxPossibleOverlap = Math.min(baseWords.length, incomingWords.length);
  let bestOverlap = 0;

  for (let k = maxPossibleOverlap; k >= 1; k--) {
    let match = true;
    for (let i = 0; i < k; i++) {
      const bWord = norm(baseWords[baseWords.length - k + i]);
      const iWord = norm(incomingWords[i]);
      if (bWord !== iWord || !bWord) {
        match = false;
        break;
      }
    }
    if (match) {
      bestOverlap = k;
      break;
    }
  }

  if (bestOverlap > 0) {
    const remainder = incomingWords.slice(bestOverlap);
    if (remainder.length === 0) {
      // If incoming was a single word (e.g. repeated filler word like "um" after "um"), preserve it!
      if (incomingWords.length === 1) {
        return `${base} ${incoming}`;
      }
      return base;
    }
    return `${base} ${remainder.join(' ')}`;
  }

  return `${base} ${incoming}`;
}

export function useSimulationAudio({
  language = SIMULATION_CONSTANTS.DEFAULT_LANGUAGE,
  personaName,
  personaVoiceConfig,
  onTranscript,
  onInterruption,
  onError,
  enabled = true,
}: UseSimulationAudioOptions) {
  const [isMuted, setIsMuted] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const isAudioLoadingRef = useRef(false);
  const [audioLoadingError, setAudioLoadingError] = useState<string | null>(null);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const audioLoadWatchdogRef = useRef<any>(null);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [silenceCountdownSeconds, setSilenceCountdownSeconds] = useState<number | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [isAutoplayBlocked, setIsAutoplayBlocked] = useState(false);
  const pendingAutoplayRef = useRef<(() => Promise<void> | void) | null>(null);

  const isMobileBrowser = typeof navigator !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '') ||
    (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent || ''))
  );

  // References for hardware, state tracking & streams
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mixedDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const [mixedAudioStream, setMixedAudioStream] = useState<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micGateRef = useRef<GainNode | null>(null);
  const micSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const recognitionRef = useRef<any>(null);
  const animFrameRef = useRef<number | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const activeAudioCleanupRef = useRef<(() => void) | null>(null);
  const activeAiSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const preloadedAudioRef = useRef<{ url: string; audio: HTMLAudioElement; ready: boolean } | null>(null);
  const lastLevelUpdateRef = useRef<number>(0);
  const lastReportedLevelRef = useRef<number>(0);
  const activeSpeechCancelRef = useRef<(() => void) | null>(null);
  const watchdogTimerRef = useRef<any>(null);
  const echoTimerRef = useRef<any>(null);
  const isEchoCooldownRef = useRef<boolean>(false);
  const silenceTimerRef = useRef<any>(null);
  const countdownIntervalRef = useRef<any>(null);
  const openingTimerRef = useRef<any>(null);
  const isSubmittingTurnRef = useRef<boolean>(false);
  const accumulatedFinalTextRef = useRef<string>('');
  const currentInterimTextRef = useRef<string>('');
  const turnLeadingHesitationRef = useRef<string>('');
  const createRecognitionRef = useRef<(() => any) | null>(null);
  const pendingStartRecognitionRef = useRef<boolean>(false);
  const speechServiceBlockedRef = useRef<boolean>(false);
  const speechErrorCountRef = useRef<number>(0);
  const lastRestartTimestampRef = useRef<number>(0);
  const consecutiveIdleEndsRef = useRef<number>(0);
  const lastSpeechEventTimestampRef = useRef<number>(Date.now());

  // Interruption / barge-in capture
  const aiSpeechWindowRef = useRef<{ startedAt: number; speaker: string } | null>(null);
  const overlapRef = useRef<{ active: boolean; startedAt: number; ms: number; lastTick: number }>({
    active: false,
    startedAt: 0,
    ms: 0,
    lastTick: 0,
  });
  const interruptionEventsRef = useRef<InterruptionEvent[]>([]);
  const deliveredInterruptionCountRef = useRef<number>(0);
  const pendingInterruptionRef = useRef<InterruptionEvent | null>(null);
  const lastInterruptedSpeechStartRef = useRef<number>(0);
  const [interruptionCount, setInterruptionCount] = useState(0);

  // Measured learner speech timings
  const turnSpeechStartRef = useRef<number>(0);
  const lastSpeechResultAtRef = useRef<number>(0);
  const listeningWindowOpenRef = useRef<number>(0);
  const sttConfidenceRef = useRef<number>(1);
  const micStreamStateRef = useRef<'idle' | 'granted' | 'denied' | 'unsupported'>('idle');
  const [micReady, setMicReady] = useState(false);
  const mobileSttOwnsMicRef = useRef(isMobileBrowser);
  // Server-Side STT Fallback Subsystem (guarantees voice capture on mobile/PWA/iOS if browser STT fails)
  const isServerSttFallbackActiveRef = useRef<boolean>(false);
  const [isServerSttFallbackActive, setIsServerSttFallbackActive] = useState<boolean>(false);
  const isTranscribingServerSttRef = useRef<boolean>(false);
  const serverSttSpeechDetectedRef = useRef<boolean>(false);
  const lastServerSttSpeechAtRef = useRef<number>(0);
  const serverSttSilenceTimerRef = useRef<any>(null);

  // Learner turn capture (see pickLearnerAudioMime)
  const learnerRecorderRef = useRef<MediaRecorder | null>(null);
  const learnerChunksRef = useRef<BlobPart[]>([]);
  const learnerRecordStartedAtRef = useRef<number>(0);

  // Synced state refs to prevent stale closures and unnecessary re-mounting
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onInterruptionRef = useRef(onInterruption);
  onInterruptionRef.current = onInterruption;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const isAiSpeakingRef = useRef(false);
  isAiSpeakingRef.current = isAiSpeaking;
  const personaNameRef = useRef(personaName);
  personaNameRef.current = personaName;
  const isCleaningUpRef = useRef(false);
  const isRecognizingRef = useRef(false);
  const currentSpeechIdRef = useRef<number>(0);

  // Global user gesture listener to unlock AudioContext and media playback on mobile devices
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleUnlockInteraction = () => {
      // 1. Resume AudioContext if suspended
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      // 2. Resume Web Speech Synthesis if paused ONLY when synthesis is the active engine
      if (
        !activeAudioRef.current &&
        activeSpeechCancelRef.current !== null &&
        typeof window !== 'undefined' &&
        window.speechSynthesis &&
        window.speechSynthesis.paused
      ) {
        try {
          window.speechSynthesis.resume();
        } catch {}
      }
    };

    window.addEventListener('touchstart', handleUnlockInteraction, { passive: true });
    window.addEventListener('touchend', handleUnlockInteraction, { passive: true });
    window.addEventListener('click', handleUnlockInteraction, { passive: true });
    window.addEventListener('pointerdown', handleUnlockInteraction, { passive: true });
    window.addEventListener('keydown', handleUnlockInteraction, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleUnlockInteraction);
      window.removeEventListener('touchend', handleUnlockInteraction);
      window.removeEventListener('click', handleUnlockInteraction);
      window.removeEventListener('pointerdown', handleUnlockInteraction);
      window.removeEventListener('keydown', handleUnlockInteraction);
    };
  }, []);

  // 0. Echo-safety gate — replaces hard-disabling the microphone track.
  //    The persona's audio is mixed into the session recording, so opening the mic
  //    during AI speech must not leak speaker bleed into that mix. A gain node in
  //    front of the recording destination closes the mix instead of the track, which
  //    keeps the analyser fed and makes speech-overlap detection possible.
  const setMicGate = useCallback((open: boolean) => {
    if (micGateRef.current) {
      try {
        micGateRef.current.gain.value = open ? 1 : 0;
      } catch {
        // Gain can throw if the context was closed mid-teardown; the state is going away anyway.
      }
    }
  }, []);

  /** Stop the running learner recorder and throw the capture away. */
  const discardLearnerRecorder = useCallback(() => {
    turnLeadingHesitationRef.current = '';
    const recorder = learnerRecorderRef.current;
    learnerRecorderRef.current = null;
    learnerChunksRef.current = [];
    if (recorder) {
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        // An already-stopped recorder throws on stop(); discarding the capture is all that matters.
      }
    }
  }, []);

  /** Begin capturing the learner's side of the next turn (no-op without a live mic). */
  const startLearnerRecorder = useCallback(() => {
    // If mobile browser, mobile Google STT owns the hardware microphone exclusively,
    // UNLESS Server-Side STT Fallback is active (in which case webkitSpeechRecognition is dormant).
    if ((isMobileBrowser || mobileSttOwnsMicRef.current) && !isServerSttFallbackActiveRef.current) {
      mobileSttOwnsMicRef.current = true;
      return;
    }
    const stream = mediaStreamRef.current;
    if (!stream || isCleaningUpRef.current) return;
    if (learnerRecorderRef.current && learnerRecorderRef.current.state === 'recording') return;
    const mimeType = pickLearnerAudioMime();
    if (!mimeType) return;
    try {
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32000 });
      learnerChunksRef.current = [];
      recorder.ondataavailable = (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) learnerChunksRef.current.push(ev.data);
      };
      recorder.onerror = () => {
        learnerRecorderRef.current = null;
        learnerChunksRef.current = [];
      };
      recorder.start(1000);
      learnerRecorderRef.current = recorder;
      learnerRecordStartedAtRef.current = Date.now();
    } catch {
      learnerRecorderRef.current = null;
    }
  }, [isMobileBrowser]);

  /** Finish the capture and resolve it as a data URL, or null when unusable. */
  const stopLearnerRecorder = useCallback(async (): Promise<string | null> => {
    const recorder = learnerRecorderRef.current;
    learnerRecorderRef.current = null;
    if (!recorder) return null;

    const recordedMs = Date.now() - (learnerRecordStartedAtRef.current || Date.now());
    const chunks = learnerChunksRef.current;
    learnerChunksRef.current = [];

    await new Promise<void>((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      try {
        if (recorder.state === 'inactive') {
          done();
          return;
        }
        recorder.onstop = done;
        recorder.stop();
        // A stuck recorder must never hold up the turn.
        setTimeout(done, 1000);
      } catch {
        done();
      }
    });

    if (!chunks.length) return null;
    const blob = new Blob(chunks as BlobPart[], {
      type: String(recorder.mimeType || 'audio/webm').split(';')[0],
    });
    if (!blob.size || blob.size > MAX_LEARNER_AUDIO_BYTES || recordedMs > MAX_LEARNER_AUDIO_MS) return null;
    try {
      return await blobToDataUrl(blob);
    } catch {
      return null;
    }
  }, []);

  const muteMicForAiSpeech = useCallback(() => {
    // Gate the Web Audio STT mix node to eliminate echo into speech recognition
    // without disabling the hardware track (preserving session recording & barge-in energy)
    setMicGate(false);
    aiSpeechWindowRef.current = aiSpeechWindowRef.current || {
      startedAt: Date.now(),
      speaker: personaNameRef.current || 'AI Persona',
    };
    // The persona has taken the floor: anything still running would record the
    // persona, not the learner, so it is dropped rather than uploaded.
    discardLearnerRecorder();
  }, [setMicGate, discardLearnerRecorder]);

  const unmuteMicAfterAiSpeech = useCallback(() => {
    setMicGate(true);
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !isMutedRef.current;
      });
    }
  }, [setMicGate]);

  const resetOverlapTracking = useCallback(() => {
    overlapRef.current = { active: false, startedAt: 0, ms: 0, lastTick: 0 };
  }, []);

  /** Log one learner cut-in for the AI turn that is currently playing. */
  const recordInterruption = useCallback((source: 'barge_in' | 'speech_overlap') => {
    const window = aiSpeechWindowRef.current;
    const now = Date.now();
    const speechStart = window ? window.startedAt : now;
    // One interruption per persona turn — repeated energy ticks must never inflate the count.
    if (lastInterruptedSpeechStartRef.current === speechStart) return;
    lastInterruptedSpeechStartRef.current = speechStart;

    const event: InterruptionEvent = {
      occurredAt: overlapRef.current.startedAt || now,
      durationMs: Math.max(0, (overlapRef.current.startedAt || now) - speechStart),
      aiSpeechStartedAt: speechStart,
      interruptedSpeaker: window?.speaker || personaNameRef.current || 'AI Persona',
      interruptedRole: 'PERSONA',
      learnerRole: 'LEARNER',
      source,
    };

    interruptionEventsRef.current = [...interruptionEventsRef.current, event];
    pendingInterruptionRef.current = pendingInterruptionRef.current || event;
    setInterruptionCount(interruptionEventsRef.current.length);
    if (onInterruptionRef.current) onInterruptionRef.current(event);
    console.info(
      `[Trainova Audio] ${source === 'barge_in' ? 'Barge-in' : 'Speech overlap'} captured — persona "${event.interruptedSpeaker}" had spoken ${event.durationMs}ms before the learner cut in.`
    );
  }, []);

  // 1. Stop Speech / Audio playback immediately (Barge-in friendly)
  const stopSpeaking = useCallback((preservePreloaded = false) => {
    currentSpeechIdRef.current += 1;

    if (echoTimerRef.current) {
      clearTimeout(echoTimerRef.current);
      echoTimerRef.current = null;
    }
    if (openingTimerRef.current) {
      clearTimeout(openingTimerRef.current);
      openingTimerRef.current = null;
    }
    isEchoCooldownRef.current = false;

    // Maintain live mic tracks so meeting recording and interruptions are never muted
    unmuteMicAfterAiSpeech();
    aiSpeechWindowRef.current = null;
    resetOverlapTracking();
    listeningWindowOpenRef.current = 0;

    if (activeAiSourceRef.current) {
      try {
        activeAiSourceRef.current.disconnect();
      } catch {}
      activeAiSourceRef.current = null;
    }

    if (activeAudioCleanupRef.current) {
      try {
        activeAudioCleanupRef.current();
      } catch {}
      activeAudioCleanupRef.current = null;
    }

    if (activeAudioRef.current) {
      try {
        const audio = activeAudioRef.current;
        audio.onplaying = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
        audio.load();
      } catch {}
      activeAudioRef.current = null;
    }
    if (!preservePreloaded && preloadedAudioRef.current) {
      try {
        const audio = preloadedAudioRef.current.audio;
        audio.onplaying = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
        audio.load();
      } catch {}
      preloadedAudioRef.current = null;
    }
    if (activeSpeechCancelRef.current) {
      try {
        activeSpeechCancelRef.current();
      } catch {}
      activeSpeechCancelRef.current = null;
    }
    stopAllSpeech();
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }
    if (audioLoadWatchdogRef.current) {
      clearTimeout(audioLoadWatchdogRef.current);
      audioLoadWatchdogRef.current = null;
    }
    setIsAudioLoading(false);
    isAudioLoadingRef.current = false;
    setIsAudioReady(false);
    setIsAiSpeaking(false);
    isAiSpeakingRef.current = false;
  }, [resetOverlapTracking, unmuteMicAfterAiSpeech]);

  // 1b. Cancel and reset active auto-submit silence countdown
  const cancelSilenceCountdown = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setSilenceCountdownSeconds(null);
  }, []);

  // Finalize complete learner utterance and submit exactly once
  const finalizeUtterance = useCallback(() => {
    cancelSilenceCountdown();

    if (isSubmittingTurnRef.current) {
      return;
    }

    // Merge any pending interim text into accumulated final text with overlap deduplication
    const pendingInterim = currentInterimTextRef.current.trim();
    let fullUtterance = accumulatedFinalTextRef.current.trim();

    if (pendingInterim) {
      fullUtterance = mergeTranscriptStrings(fullUtterance, pendingInterim);
    }

    // Safety fallback: if learner spoke an initial hesitation word that was held in interim
    // but both final and interim cleared unexpectedly, preserve the captured leading hesitation
    if (!fullUtterance && turnLeadingHesitationRef.current) {
      fullUtterance = turnLeadingHesitationRef.current;
    }

    // Clean dots, ellipsis, and placeholder marks from active speech
    const cleanUtterance = fullUtterance.replace(/^[.\s…]+|[.\s…]+$/g, '').trim();

    // Prevent duplicate submissions and ensure only valid, non-empty responses are submitted
    const isValid = cleanUtterance.length > 0 &&
                    cleanUtterance !== '...' &&
                    cleanUtterance !== '..' &&
                    !cleanUtterance.startsWith('🎙️');

    turnLeadingHesitationRef.current = '';
    accumulatedFinalTextRef.current = '';
    currentInterimTextRef.current = '';
    setInterimTranscript('');

    if (isValid && !isCleaningUpRef.current && onTranscriptRef.current) {
      isSubmittingTurnRef.current = true;
      console.info(`[Trainova Audio] Submitting finalized learner turn: "${cleanUtterance}"`);

      const speechEndMs = Date.now();
      const speechStartMs = turnSpeechStartRef.current || 0;
      const lastResultAt = lastSpeechResultAtRef.current || speechEndMs;
      const windowOpen = listeningWindowOpenRef.current || 0;

      // Only the events captured since the previous finalized turn, so each cut-in is
      // attributed to exactly one learner turn instead of replaying the whole history.
      const turnInterruptions = interruptionEventsRef.current.slice(deliveredInterruptionCountRef.current);
      deliveredInterruptionCountRef.current = interruptionEventsRef.current.length;

      const meta: LearnerTurnSpeechMeta = {
        speechStartMs: speechStartMs || undefined,
        speechEndMs,
        // Real microphone time, not the wall clock including the silence-finalization timer
        durationMs: speechStartMs ? Math.max(0, lastResultAt - speechStartMs) : undefined,
        hesitationMs: speechStartMs && windowOpen ? Math.max(0, speechStartMs - windowOpen) : undefined,
        wordCount: cleanUtterance.split(/\s+/).filter(Boolean).length,
        sttConfidence: sttConfidenceRef.current,
        interruption: pendingInterruptionRef.current || turnInterruptions[0] || null,
        interruptions: turnInterruptions,
      };

      pendingInterruptionRef.current = null;
      turnSpeechStartRef.current = 0;
      lastSpeechResultAtRef.current = 0;
      sttConfidenceRef.current = 1;
      // Hand the captured utterance to the turn request; the transcript is emitted
      // immediately and the audio resolves alongside it.
      meta.audioPromise = stopLearnerRecorder();

      onTranscriptRef.current(cleanUtterance, meta);

      setTimeout(() => {
        isSubmittingTurnRef.current = false;
      }, 500);
    } else {
      discardLearnerRecorder();
      isSubmittingTurnRef.current = false;
    }
  }, [cancelSilenceCountdown, stopLearnerRecorder, discardLearnerRecorder]);

  // 1c. Initiate 5-second auto-submit countdown when silence is confirmed after learner speech
  const startSilenceCountdown = useCallback(() => {
    // Check if there is valid, non-empty speech text captured
    const currentAcc = accumulatedFinalTextRef.current.trim();
    const interim = currentInterimTextRef.current.trim();
    let fullHypothesis = currentAcc;
    if (interim) {
      fullHypothesis = mergeTranscriptStrings(currentAcc, interim);
    }
    const clean = fullHypothesis.replace(/^[.\s…]+|[.\s…]+$/g, '').trim();
    const hasValidSpeech = clean.length > 0 &&
                           clean !== '...' &&
                           clean !== '..' &&
                           !clean.startsWith('🎙️');

    if (!hasValidSpeech || isCleaningUpRef.current || isAiSpeakingRef.current || isSubmittingTurnRef.current) {
      return;
    }

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Display 5-second countdown with a clear "Submit Response" button for manual submission
    let remaining = 5;
    setSilenceCountdownSeconds(remaining);

    countdownIntervalRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setSilenceCountdownSeconds(remaining);
      } else {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setSilenceCountdownSeconds(null);
        // Automatically submit the captured response when countdown expires
        finalizeUtterance();
      }
    }, 1000);
  }, [finalizeUtterance]);

  const flushAndFinalize = useCallback(() => {
    cancelSilenceCountdown();
    finalizeUtterance();
  }, [cancelSilenceCountdown, finalizeUtterance]);

  // 2. Safe SpeechRecognition Controllers
  const startRecognitionSafely = useCallback((force = false) => {
    if (
      isCleaningUpRef.current ||
      isMutedRef.current ||
      !enabledRef.current ||
      isAiSpeakingRef.current ||
      isAudioLoadingRef.current ||
      isEchoCooldownRef.current ||
      speechServiceBlockedRef.current ||
      (activeAudioRef.current && !activeAudioRef.current.paused) ||
      (activeSpeechCancelRef.current !== null && typeof window !== 'undefined' && window.speechSynthesis?.speaking)
    ) {
      console.debug('[Trainova AudioDiag:STT] startRecognitionSafely blocked by guard:', {
        cleanup: isCleaningUpRef.current,
        muted: isMutedRef.current,
        enabled: enabledRef.current,
        aiSpeaking: isAiSpeakingRef.current,
        audioLoading: isAudioLoadingRef.current,
        echoCooldown: isEchoCooldownRef.current,
        blocked: speechServiceBlockedRef.current,
        audioPlaying: Boolean(activeAudioRef.current && !activeAudioRef.current.paused),
        synthSpeaking: Boolean(activeSpeechCancelRef.current !== null && typeof window !== 'undefined' && window.speechSynthesis?.speaking),
      });
      return;
    }

    const now = Date.now();
    // Prevent thrashing restarts within 350ms (an explicit barge-in bypasses this)
    if (!force && now - lastRestartTimestampRef.current < 350) {
      return;
    }
    lastRestartTimestampRef.current = now;

    if (isRecognizingRef.current) {
      setIsListening(true);
      return;
    }

    if (isServerSttFallbackActiveRef.current) {
      setIsListening(true);
      startLearnerRecorder();
      return;
    }

    // On WebKit/Chromium (especially mobile), ended or aborted sessions cannot be reused via start();
    // they MUST be replaced with a fresh SpeechRecognition instance.
    if (createRecognitionRef.current) {
      pendingStartRecognitionRef.current = false;
      try {
        if (recognitionRef.current) {
          try {
            recognitionRef.current.onstart = null;
            recognitionRef.current.onend = null;
            recognitionRef.current.onerror = null;
            recognitionRef.current.onresult = null;
            recognitionRef.current.abort();
          } catch {}
          recognitionRef.current = null;
        }

        const freshRec = createRecognitionRef.current();
        if (freshRec) {
          recognitionRef.current = freshRec;
          // On mobile, guarantee no active MediaStreamTrack is holding the Android Audio HAL
          if (isMobileBrowser && mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
          }
          try {
            freshRec.start();
            isRecognizingRef.current = true;
            setIsListening(true);
            console.info('[Trainova AudioDiag:STT] Started fresh SpeechRecognition session. Listening active.');
            // The learner now holds the floor — capture this turn's audio for replay.
            startLearnerRecorder();
          } catch (startErr: any) {
            console.warn('[Trainova AudioDiag:STT] Fresh recognition start note:', startErr?.message || startErr);
            isRecognizingRef.current = false;
            if (startErr?.name === 'InvalidStateError' || /already started/i.test(startErr?.message || '')) {
              setTimeout(() => {
                if (!isCleaningUpRef.current && !isMutedRef.current && enabledRef.current && !isAiSpeakingRef.current) {
                  startRecognitionSafely(true);
                }
              }, 250);
            }
          }
        }
      } catch (err: any) {
        console.warn('[Trainova AudioDiag:STT] Fresh recognition start error:', err?.message || err);
      }
    } else {
      // Recognizer factory is not ready yet (e.g. during mount / permission resolution).
      // Mark pending so initialization automatically triggers recognition upon completion.
      pendingStartRecognitionRef.current = true;
    }
  }, [startLearnerRecorder]);

  const stopRecognitionSafely = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
    }
    isRecognizingRef.current = false;
    setIsListening(false);
  }, []);

  const abortRecognitionSafely = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {}
    }
    isRecognizingRef.current = false;
    setIsListening(false);
  }, []);

  // 2b. Cleanly transition out of AI speech into learner listening mode
  const concludeAiSpeech = useCallback((onEndCallback?: () => void) => {
    if (audioLoadWatchdogRef.current) {
      clearTimeout(audioLoadWatchdogRef.current);
      audioLoadWatchdogRef.current = null;
    }
    setIsAudioLoading(false);
    isAudioLoadingRef.current = false;
    setIsAudioReady(false);
    setIsAiSpeaking(false);
    isAiSpeakingRef.current = false;
    setInterimTranscript('');
    if (activeAiSourceRef.current) {
      try {
        activeAiSourceRef.current.disconnect();
      } catch {}
      activeAiSourceRef.current = null;
    }
    activeAudioRef.current = null;
    activeSpeechCancelRef.current = null;
    aiSpeechWindowRef.current = null;
    resetOverlapTracking();

    if (echoTimerRef.current) clearTimeout(echoTimerRef.current);
    // Brief 600ms cooldown to allow audio acoustics to clear
    echoTimerRef.current = setTimeout(() => {
      isEchoCooldownRef.current = false;
      unmuteMicAfterAiSpeech();
      setIsListening(!isMutedRef.current);
      // Marks the start of the learner's response window — used to measure hesitation pauses
      listeningWindowOpenRef.current = Date.now();
      turnSpeechStartRef.current = 0;
      if (onEndCallback) onEndCallback();
      startRecognitionSafely();
    }, 600);
  }, [resetOverlapTracking, startRecognitionSafely, unmuteMicAfterAiSpeech]);

  // 2c. Explicit barge-in: kill the persona's voice and hand the floor to the learner now.
  //     Records the interruption against the persona turn that was cut off, then restarts
  //     listening immediately (no echo cooldown, no restart throttle).
  const bargeIn = useCallback((source: 'barge_in' | 'speech_overlap' = 'barge_in') => {
    const wasSpeaking =
      isAiSpeakingRef.current ||
      Boolean(activeAudioRef.current && !activeAudioRef.current.paused) ||
      Boolean(activeSpeechCancelRef.current) ||
      Boolean(aiSpeechWindowRef.current);

    if (wasSpeaking) {
      recordInterruption(source);
    }

    stopSpeaking();
    speechServiceBlockedRef.current = false;
    speechErrorCountRef.current = 0;
    consecutiveIdleEndsRef.current = 0;
    isEchoCooldownRef.current = false;
    lastRestartTimestampRef.current = 0;
    turnLeadingHesitationRef.current = '';
    accumulatedFinalTextRef.current = '';
    currentInterimTextRef.current = '';
    sttConfidenceRef.current = 1;
    turnSpeechStartRef.current = Date.now();
    lastSpeechResultAtRef.current = Date.now();
    listeningWindowOpenRef.current = listeningWindowOpenRef.current || Date.now();
    setInterimTranscript('');

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }

    setIsMuted(false);
    isMutedRef.current = false;
    setMicPermissionDenied(false);
    startRecognitionSafely(true);
    return wasSpeaking;
  }, [recordInterruption, startRecognitionSafely, stopSpeaking]);

  // 2d. Microphone capture graph — shared by mount, retry and the pre-turn readiness gate.
  //     mic → analyser (always live: feeds the level meter and overlap detection)
  //         → gate → mixedDestination (the session recording, closed while the persona speaks)
  const attachMicrophoneStream = useCallback((stream: MediaStream) => {
    mediaStreamRef.current = stream;
    setMicPermissionDenied(false);
    micStreamStateRef.current = 'granted';
    setMicReady(true);
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !isMutedRef.current;
    });

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new AudioContextClass();
      analyserRef.current = null;
      micGateRef.current = null;
      mixedDestinationRef.current = null;
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    if (!analyserRef.current) {
      if (micSourceNodeRef.current) {
        try {
          micSourceNodeRef.current.disconnect();
        } catch {}
      }
      const source = ctx.createMediaStreamSource(stream);
      micSourceNodeRef.current = source;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = SIMULATION_CONSTANTS.AUDIO_FFT_SIZE;
      source.connect(analyser);
      analyserRef.current = analyser;

      if (!mixedDestinationRef.current) {
        mixedDestinationRef.current = ctx.createMediaStreamDestination();
        setMixedAudioStream(mixedDestinationRef.current.stream);
      }
      const gate = ctx.createGain();
      gate.gain.value = aiSpeechWindowRef.current ? 0 : 1;
      micGateRef.current = gate;
      analyser.connect(gate);
      gate.connect(mixedDestinationRef.current);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateAudioLevel = () => {
        if (!analyserRef.current || isCleaningUpRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;
        const level = Math.min(100, Math.round((average / 255) * 100));

        // Throttle UI React state updates: ~15fps max (every 65ms) or when crossing speaking threshold (15)
        const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const crossedSpeakingBoundary =
          (lastReportedLevelRef.current <= 15 && level > 15) ||
          (lastReportedLevelRef.current > 15 && level <= 15);
        const timeElapsed = nowMs - lastLevelUpdateRef.current >= 65;
        const significantDelta = Math.abs(level - lastReportedLevelRef.current) >= 4;

        if (crossedSpeakingBoundary || (timeElapsed && significantDelta) || (timeElapsed && level === 0 && lastReportedLevelRef.current !== 0)) {
          lastLevelUpdateRef.current = nowMs;
          lastReportedLevelRef.current = level;
          setAudioLevel(level);
        }

        // Passive speech-overlap detection: learner mic energy while the persona
        // still holds the floor. Only fires while an AI speech window is open.
        if (aiSpeechWindowRef.current) {
          const nowTs = Date.now();
          if (level >= OVERLAP_LEVEL_THRESHOLD) {
            if (!overlapRef.current.active) {
              overlapRef.current = { active: true, startedAt: nowTs, ms: 0, lastTick: nowTs };
              recordInterruption('speech_overlap');
            } else {
              overlapRef.current.ms += nowTs - overlapRef.current.lastTick;
              overlapRef.current.lastTick = nowTs;
            }
          } else if (overlapRef.current.active && nowTs - overlapRef.current.lastTick > 400) {
            resetOverlapTracking();
          }
        } else if (overlapRef.current.active) {
          resetOverlapTracking();
        }

        // Server-Side STT Fallback VAD & Silence Finalization
        if (
          isServerSttFallbackActiveRef.current &&
          !isAiSpeakingRef.current &&
          !isAudioLoadingRef.current &&
          !isMutedRef.current &&
          !isTranscribingServerSttRef.current
        ) {
          const nowTs = Date.now();
          if (level >= 15) {
            serverSttSpeechDetectedRef.current = true;
            lastServerSttSpeechAtRef.current = nowTs;
          } else if (
            serverSttSpeechDetectedRef.current &&
            nowTs - lastServerSttSpeechAtRef.current > (isMobileBrowser ? 1800 : 2500)
          ) {
            serverSttSpeechDetectedRef.current = false;
            stopLearnerRecorder().then((audioDataUrl) => {
              if (audioDataUrl) {
                processServerSttRecording(audioDataUrl);
              }
            });
          }
        }

        animFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };
      updateAudioLevel();
    } else if (mixedDestinationRef.current) {
      // Re-attach a refreshed mic stream onto the existing analyser → gate → mix graph
      // (analyser and gate remain permanently connected to mixedDestination; we only swap the source)
      try {
        if (micSourceNodeRef.current) {
          try {
            micSourceNodeRef.current.disconnect();
          } catch {}
        }
        const source = ctx.createMediaStreamSource(stream);
        micSourceNodeRef.current = source;
        if (analyserRef.current) {
          source.connect(analyserRef.current);
        } else if (micGateRef.current) {
          source.connect(micGateRef.current);
        } else if (mixedDestinationRef.current) {
          source.connect(mixedDestinationRef.current);
        }
      } catch (connErr) {
        console.debug('[Trainova Audio] AudioContext reconnect note:', connErr);
      }
    }
  }, [recordInterruption, resetOverlapTracking]);

  // 2c. Server-Side STT Fallback Processor
  const processServerSttRecording = useCallback(
    async (audioDataUrl: string) => {
      if (!audioDataUrl || isCleaningUpRef.current || isTranscribingServerSttRef.current) return;
      isTranscribingServerSttRef.current = true;
      try {
        const res = await simulationService.transcribeAudio(audioDataUrl, language);
        const recognizedText = res?.data?.transcript || (res?.data as any)?.text || '';
        if (recognizedText.trim() && !isCleaningUpRef.current) {
          accumulatedFinalTextRef.current = recognizedText.trim();
          setInterimTranscript(recognizedText.trim());
          finalizeUtterance();
        } else {
          setInterimTranscript('');
          if (!isAiSpeakingRef.current && !isMutedRef.current && enabledRef.current) {
            startLearnerRecorder();
          }
        }
      } catch (err: any) {
        console.warn('[Trainova Audio] Server STT fallback error:', err?.message || err);
        setInterimTranscript('');
        if (!isAiSpeakingRef.current && !isMutedRef.current && enabledRef.current) {
          startLearnerRecorder();
        }
      } finally {
        isTranscribingServerSttRef.current = false;
      }
    },
    [language, finalizeUtterance, startLearnerRecorder]
  );

  const activateServerSttFallback = useCallback(
    async (reason: string) => {
      if (isServerSttFallbackActiveRef.current || isCleaningUpRef.current) return;
      console.info(`[Trainova Audio] Activating Server-Side STT Fallback (${reason}).`);
      isServerSttFallbackActiveRef.current = true;
      setIsServerSttFallbackActive(true);
      speechServiceBlockedRef.current = false;
      abortRecognitionSafely();

      if (!mediaStreamRef.current) {
        try {
          const stream = await acquireMicrophoneStream();
          attachMicrophoneStream(stream);
        } catch (e: any) {
          console.warn('[Trainova Audio] Server STT fallback mic acquisition note:', e?.message || e);
        }
      }
      setMicPermissionDenied(false);
      micStreamStateRef.current = 'granted';
      setMicReady(true);
      setIsListening(true);
      startLearnerRecorder();
    },
    [abortRecognitionSafely, attachMicrophoneStream, startLearnerRecorder]
  );

  // 2e. Microphone readiness gate — resolve BEFORE the persona's opening line plays so
  //     permission is already granted and a barge-in on turn 1 can be captured.
  //     The room also starts this check while it is still loading, and the opening turn
  //     can request it at the same moment. Sharing one in-flight promise keeps that
  //     overlap from firing two getUserMedia prompts at the learner.
  const micCheckInFlightRef = useRef<Promise<boolean> | null>(null);
  const ensureMicrophone = useCallback(async (): Promise<boolean> => {
    if (micCheckInFlightRef.current) return micCheckInFlightRef.current;

    const runCheck = async (): Promise<boolean> => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
        micStreamStateRef.current = 'unsupported';
        setMicReady(false);
        return false;
      }
      if (mediaStreamRef.current) {
        const hasLiveTracks = mediaStreamRef.current.getAudioTracks().some((t) => t.readyState === 'live');
        if (hasLiveTracks) {
          micStreamStateRef.current = 'granted';
          setMicReady(true);
          return true;
        }
      }
      if (isMobileBrowser || mobileSttOwnsMicRef.current) {
        // Mobile: permission is handled natively by Web Speech API, hardware is intentionally handed to Google STT exclusively
        mobileSttOwnsMicRef.current = true;
        micStreamStateRef.current = 'granted';
        setMicReady(true);
        setMicPermissionDenied(false);
        return true;
      }
      try {
        const stream = await acquireMicrophoneStream();
        attachMicrophoneStream(stream);
        micStreamStateRef.current = 'granted';
        setMicReady(true);
        return true;
      } catch (err: any) {
        console.warn('[Trainova Audio] Pre-turn microphone readiness check failed:', err?.message || err);
        const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
        micStreamStateRef.current = denied ? 'denied' : 'idle';
        setMicReady(false);
        if (denied) setMicPermissionDenied(true);
        return false;
      }
    };

    const check = runCheck();
    micCheckInFlightRef.current = check;
    try {
      return await check;
    } finally {
      if (micCheckInFlightRef.current === check) {
        micCheckInFlightRef.current = null;
      }
    }
  }, [attachMicrophoneStream, isMobileBrowser]);

  // 2f. Audio asset preloader & decoder gate: loads and decodes the audio asset completely
  //     (waiting for canplaythrough and readyState === 4) BEFORE any playback is initiated.
  const preloadAndDecodeAudio = useCallback(async (audioUrl: string): Promise<boolean> => {
    if (!audioUrl) return true;
    if (isCleaningUpRef.current) return false;

    // If this URL is already preloaded, decoded, and ready, return true immediately
    if (
      preloadedAudioRef.current &&
      preloadedAudioRef.current.url === audioUrl &&
      preloadedAudioRef.current.ready
    ) {
      setIsAudioReady(true);
      return true;
    }

    setIsAudioLoading(true);
    isAudioLoadingRef.current = true;
    setAudioLoadingError(null);
    setIsAudioReady(false);

    return new Promise<boolean>((resolve) => {
      let resolved = false;
      let watchdogTimer: any = null;

      const audio = new Audio();
      audio.preload = 'auto';
      if (!audioUrl.startsWith('data:')) {
        audio.crossOrigin = 'anonymous';
      }

      const cleanup = () => {
        if (watchdogTimer) clearTimeout(watchdogTimer);
        audio.removeEventListener('canplaythrough', onCanPlayThrough);
        audio.removeEventListener('error', onError);
      };

      const finish = (success: boolean) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        setIsAudioLoading(false);
        isAudioLoadingRef.current = false;
        if (success && !isCleaningUpRef.current) {
          preloadedAudioRef.current = { url: audioUrl, audio, ready: true };
          setIsAudioReady(true);
        } else {
          try {
            audio.src = '';
            audio.load();
          } catch {}
          setIsAudioReady(false);
        }
        resolve(success);
      };

      const onCanPlayThrough = () => {
        finish(true);
      };

      const onError = () => {
        console.warn('[Trainova Audio] Preload failed for audio asset');
        finish(false);
      };

      audio.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
      audio.addEventListener('error', onError, { once: true });

      watchdogTimer = setTimeout(() => {
        console.warn('[Trainova Audio] Audio preload timed out (3s)');
        finish(false);
      }, 3000);

      audio.src = audioUrl;

      if (audio.readyState >= 3) {
        finish(true);
      }
    });
  }, []);

  // 3. Fallback Speech Synthesis with Persona Voice Matching
  const speakSynthesis = useCallback(
    (
      text: string,
      onEnd?: () => void,
      customPersonaName?: string | PersonaVoiceConfig,
      isRetry = false,
      skipStop = false
    ) => {
      if (isCleaningUpRef.current) {
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        return;
      }
      if (!text || !text.trim()) {
        concludeAiSpeech(onEnd);
        return;
      }
      const vConfig: PersonaVoiceConfig =
        typeof customPersonaName === 'string'
          ? { personaName: customPersonaName }
          : ((customPersonaName as any) || personaVoiceConfig || {});

      try {
        if (!skipStop && !isRetry) {
          stopSpeaking();
        }
        if (isCleaningUpRef.current) return;
        const thisSpeechId = ++currentSpeechIdRef.current;

        // Synchronously enter audio loading & echo cooldown state; do NOT flag as speaking until audio actually starts
        setIsAudioLoading(true);
        isAudioLoadingRef.current = true;
        setAudioLoadingError(null);
        setIsAudioReady(false);
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        isEchoCooldownRef.current = true;

        // Synchronously abort recognition and gate the mic mix (never the hardware track,
        // so overlap energy stays measurable for barge-in detection)
        abortRecognitionSafely();
        muteMicForAiSpeech();
        turnLeadingHesitationRef.current = '';
        accumulatedFinalTextRef.current = '';
        currentInterimTextRef.current = '';
        setInterimTranscript('');
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setSilenceCountdownSeconds(null);

        const effectivePersonaName = vConfig.personaName || personaName;

        // Generous start watchdog (3500ms): on mobile/slow devices, voice initialization can take 2-4 seconds.
        // If speaking has not started within 3500ms and is NOT actively speaking, recover gracefully.
        const mobileSpeechStartWatchdog = setTimeout(() => {
          if (thisSpeechId !== currentSpeechIdRef.current) return;

          // If browser speech synthesis is actively speaking, do NOT abort!
          if (typeof window !== 'undefined' && window.speechSynthesis && window.speechSynthesis.speaking) {
            return;
          }

          if (isAudioLoadingRef.current || isAiSpeakingRef.current) {
            // Check if browser blocked speech synthesis due to autoplay / user gesture
            const isUserActivationMissing = typeof navigator !== 'undefined' &&
              (navigator as any).userActivation &&
              !(navigator as any).userActivation.hasBeenActive;
            const isSpeechPausedOrPending = typeof window !== 'undefined' &&
              window.speechSynthesis &&
              (window.speechSynthesis.paused || window.speechSynthesis.pending);

            if ((isUserActivationMissing || isSpeechPausedOrPending) && !isCleaningUpRef.current) {
              console.warn('[Trainova Audio] Speech synthesis blocked by browser autoplay/interaction policy. Prompting for interaction.');
              isEchoCooldownRef.current = false;
              unmuteMicAfterAiSpeech();
              setIsAutoplayBlocked(true);
              setIsAudioLoading(false);
              isAudioLoadingRef.current = false;
              setIsAiSpeaking(false);
              isAiSpeakingRef.current = false;
              activeSpeechCancelRef.current = null;
              if (typeof window !== 'undefined' && window.speechSynthesis) {
                try {
                  window.speechSynthesis.cancel();
                } catch {}
              }
              pendingAutoplayRef.current = async () => {
                setIsAutoplayBlocked(false);
                try {
                  if (typeof window !== 'undefined' && window.speechSynthesis) {
                    if (window.speechSynthesis.paused) {
                      window.speechSynthesis.resume();
                    }
                  }
                } catch {}
                speakSynthesis(text, onEnd, vConfig, false, true);
              };
              startRecognitionSafely();
              return;
            }

            if (!isRetry && !isCleaningUpRef.current) {
              console.warn('[Trainova Audio] Speech synthesis start stalled. Retrying with local system default voice.');
              if (typeof window !== 'undefined' && window.speechSynthesis) {
                try {
                  if (window.speechSynthesis.paused) {
                    window.speechSynthesis.resume();
                  }
                } catch {}
              }
              speakSynthesis(
                text,
                onEnd,
                { ...vConfig, ttsVoice: undefined, country: undefined },
                true,
                true
              );
              return;
            }

            console.warn('[Trainova Audio] Speech synthesis stalled. Retaining persona floor with autoplay / replay recovery.');
            isEchoCooldownRef.current = false;
            unmuteMicAfterAiSpeech();
            setIsAudioLoading(false);
            isAudioLoadingRef.current = false;
            setIsAiSpeaking(false);
            isAiSpeakingRef.current = false;
            activeSpeechCancelRef.current = null;
            if (typeof window !== 'undefined' && window.speechSynthesis) {
              try {
                window.speechSynthesis.cancel();
              } catch {}
            }
            setIsAutoplayBlocked(true);
            pendingAutoplayRef.current = async () => {
              setIsAutoplayBlocked(false);
              speakSynthesis(text, onEnd, vConfig, false, true);
            };
            startRecognitionSafely();
          }
        }, 3500);

        const isHindi = language.toLowerCase().startsWith('hi');
        const candidateVoice = isRetry ? undefined : (vConfig.ttsVoice || personaVoiceConfig?.ttsVoice);
        const effectiveTtsVoice = isHindi
          ? (candidateVoice && (candidateVoice.toLowerCase().includes('hi-') || candidateVoice.toLowerCase().includes('hindi')) ? candidateVoice : undefined)
          : candidateVoice;

        const handle = speakSpeechSynthesis({
          text,
          role: 'persona',
          personaName: effectivePersonaName,
          lang: isHindi ? 'hi-IN' : language,
          country: isRetry ? undefined : (vConfig.country || personaVoiceConfig?.country),
          tone: vConfig.tone || personaVoiceConfig?.tone,
          ttsVoice: effectiveTtsVoice,
          genderPreference: vConfig.genderPreference || personaVoiceConfig?.genderPreference,
          pitch: typeof vConfig.pitch === 'number' ? vConfig.pitch : personaVoiceConfig?.pitch,
          rate: typeof vConfig.rate === 'number' ? vConfig.rate : personaVoiceConfig?.rate,
          onStart: () => {
            clearTimeout(mobileSpeechStartWatchdog);
            if (isCleaningUpRef.current || thisSpeechId !== currentSpeechIdRef.current) {
              stopSpeaking();
              return;
            }
            setIsAudioLoading(false);
            isAudioLoadingRef.current = false;
            setAudioLoadingError(null);
            setIsAudioReady(true);
            setInterimTranscript('');
            setIsAiSpeaking(true);
            isAiSpeakingRef.current = true;
            isEchoCooldownRef.current = true;
            abortRecognitionSafely();
            muteMicForAiSpeech();
          },
          onEnd: () => {
            clearTimeout(mobileSpeechStartWatchdog);
            if (thisSpeechId !== currentSpeechIdRef.current) return;
            setIsAudioLoading(false);
            isAudioLoadingRef.current = false;
            activeSpeechCancelRef.current = null;
            concludeAiSpeech(onEnd);
          },
          onError: (err: any) => {
            clearTimeout(mobileSpeechStartWatchdog);
            if (thisSpeechId !== currentSpeechIdRef.current) return;
            console.warn('[Trainova] Speech synthesis error:', err);

            // If browser blocked speech synthesis due to autoplay/permission, prompt for interaction
            const isNotAllowed = err?.error === 'not-allowed' || err?.isAutoplayBlocked;
            if (isNotAllowed && !isCleaningUpRef.current) {
              console.warn('[Trainova Audio] Speech synthesis not allowed by policy. Showing tap-to-enable banner.');
              isEchoCooldownRef.current = false;
              unmuteMicAfterAiSpeech();
              setIsAutoplayBlocked(true);
              setIsAudioLoading(false);
              isAudioLoadingRef.current = false;
              setIsAiSpeaking(false);
              isAiSpeakingRef.current = false;
              activeSpeechCancelRef.current = null;
              if (typeof window !== 'undefined' && window.speechSynthesis) {
                try {
                  window.speechSynthesis.cancel();
                } catch {}
              }
              pendingAutoplayRef.current = async () => {
                setIsAutoplayBlocked(false);
                speakSynthesis(text, onEnd, vConfig, false, true);
              };
              startRecognitionSafely();
              return;
            }

            // Attempt seamless fallback retry with local system default voice
            if (!isRetry && !isCleaningUpRef.current) {
              console.warn('[Trainova Audio] Speech synthesis failed. Attempting fallback retry with local default voice.');
              if (typeof window !== 'undefined' && window.speechSynthesis) {
                try {
                  if (window.speechSynthesis.paused) {
                    window.speechSynthesis.resume();
                  }
                } catch {}
              }
              setTimeout(() => {
                if (isCleaningUpRef.current || thisSpeechId !== currentSpeechIdRef.current) return;
                speakSynthesis(
                  text,
                  onEnd,
                  { ...vConfig, ttsVoice: undefined, country: undefined },
                  true,
                  true
                );
              }, 60);
              return;
            }

            // Both attempts exhausted; do NOT silently switch floor to learner without speaking!
            // Flag audio error and setup autoplay/replay recovery so the learner can hear the AI turn
            isEchoCooldownRef.current = false;
            unmuteMicAfterAiSpeech();
            setIsAudioLoading(false);
            isAudioLoadingRef.current = false;
            setIsAiSpeaking(false);
            isAiSpeakingRef.current = false;
            activeSpeechCancelRef.current = null;
            if (typeof window !== 'undefined' && window.speechSynthesis) {
              try {
                window.speechSynthesis.cancel();
              } catch {}
            }
            setAudioLoadingError('Speech synthesis was interrupted. Tap anywhere to replay.');
            setIsAutoplayBlocked(true);
            pendingAutoplayRef.current = async () => {
              setIsAutoplayBlocked(false);
              setAudioLoadingError(null);
              speakSynthesis(text, onEnd, vConfig, false, true);
            };
            startRecognitionSafely();
          },
        });
        activeSpeechCancelRef.current = handle.cancel;
      } catch (err: any) {
        console.warn('SpeechSynthesis error:', err);
        isEchoCooldownRef.current = false;
        unmuteMicAfterAiSpeech();
        setIsAudioLoading(false);
        isAudioLoadingRef.current = false;
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        activeSpeechCancelRef.current = null;
        if (typeof window !== 'undefined' && window.speechSynthesis) {
          try {
            window.speechSynthesis.cancel();
          } catch {}
        }
        setAudioLoadingError('Speech synthesis unavailable. Tap to retry.');
        setIsAutoplayBlocked(true);
        pendingAutoplayRef.current = async () => {
          setIsAutoplayBlocked(false);
          setAudioLoadingError(null);
          speakSynthesis(text, onEnd, vConfig, false, true);
        };
        startRecognitionSafely();
      }
    },
    [language, personaName, personaVoiceConfig, stopSpeaking, abortRecognitionSafely, concludeAiSpeech, unmuteMicAfterAiSpeech]
  );

  // 4. Speak Persona Text (audioUrl with base64 audio priority, falling back to Web Speech Synthesis)
  const speakText = useCallback(
    (text: string, audioUrl?: string, onEnd?: () => void, customPersonaOrConfig?: string | PersonaVoiceConfig) => {
      if (isCleaningUpRef.current) {
        setIsAudioLoading(false);
        isAudioLoadingRef.current = false;
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        return;
      }
      stopSpeaking(true);
      if (isCleaningUpRef.current) return;

      const thisSpeechId = ++currentSpeechIdRef.current;

      if (!text && !audioUrl) {
        setIsAudioLoading(false);
        isAudioLoadingRef.current = false;
        concludeAiSpeech(onEnd);
        return;
      }

      const vConfig: PersonaVoiceConfig =
        typeof customPersonaOrConfig === 'string'
          ? { personaName: customPersonaOrConfig }
          : (customPersonaOrConfig || personaVoiceConfig || {});

      // Audio loading begins; AI is NOT speaking yet until player is fully loaded and ready
      setIsAudioLoading(true);
      isAudioLoadingRef.current = true;
      setAudioLoadingError(null);
      setIsAudioReady(false);
      setIsAiSpeaking(false);
      isAiSpeakingRef.current = false;
      isEchoCooldownRef.current = true;

      // Synchronously abort recognition and gate the mic mix immediately to prevent background capture
      abortRecognitionSafely();
      muteMicForAiSpeech();
      if (vConfig.personaName || personaName) {
        aiSpeechWindowRef.current = {
          startedAt: aiSpeechWindowRef.current?.startedAt || Date.now(),
          speaker: vConfig.personaName || personaName || 'AI Persona',
        };
      }
      turnLeadingHesitationRef.current = '';
      accumulatedFinalTextRef.current = '';
      currentInterimTextRef.current = '';
      setInterimTranscript('');
      cancelSilenceCountdown();
      if (serverSttSilenceTimerRef.current) {
        clearTimeout(serverSttSilenceTimerRef.current);
        serverSttSilenceTimerRef.current = null;
      }

      // If persona designated browser speech synthesis or no audioUrl is available, route directly to browser engine
      if (vConfig.ttsProvider === 'browser' || !audioUrl) {
        if (text) {
          speakSynthesis(text, onEnd, vConfig, false, true);
        } else {
          setIsAudioLoading(false);
          isAudioLoadingRef.current = false;
          concludeAiSpeech(onEnd);
        }
        return;
      }

      if (audioUrl) {
        let fallbackTriggered = false;
        const triggerFallback = (reason?: string) => {
          if (fallbackTriggered || thisSpeechId !== currentSpeechIdRef.current) return;
          fallbackTriggered = true;
          if (activeAudioCleanupRef.current) {
            try {
              activeAudioCleanupRef.current();
            } catch {}
            activeAudioCleanupRef.current = null;
          }
          if (audioLoadWatchdogRef.current) {
            clearTimeout(audioLoadWatchdogRef.current);
            audioLoadWatchdogRef.current = null;
          }
          setIsAudioLoading(false);
          isAudioLoadingRef.current = false;
          setIsAiSpeaking(false);
          isAiSpeakingRef.current = false;
          if (activeAudioRef.current) {
            try {
              const a = activeAudioRef.current;
              a.onplaying = null;
              a.onplay = null;
              a.onended = null;
              a.onerror = null;
              a.pause();
              a.src = '';
              a.load();
            } catch {}
            activeAudioRef.current = null;
          }
          if (reason) {
            setAudioLoadingError(reason);
          }
          if (text) {
            speakSynthesis(text, onEnd, vConfig, false, true);
          } else {
            concludeAiSpeech(onEnd);
          }
        };

        try {
          let audio: HTMLAudioElement;
          const isReusingPreloaded = Boolean(
            preloadedAudioRef.current &&
            preloadedAudioRef.current.url === audioUrl &&
            preloadedAudioRef.current.ready
          );

          if (isReusingPreloaded && preloadedAudioRef.current) {
            audio = preloadedAudioRef.current.audio;
            preloadedAudioRef.current = null;
          } else {
            audio = new Audio();
            audio.preload = 'auto';
            // Only set crossOrigin on remote HTTP(S) URLs to prevent CORS errors on data: URIs
            if (!audioUrl.startsWith('data:')) {
              audio.crossOrigin = 'anonymous';
            }
          }
          activeAudioRef.current = audio;

          // 7-second audio loading watchdog timer to prevent indefinite stall
          if (audioLoadWatchdogRef.current) clearTimeout(audioLoadWatchdogRef.current);
          audioLoadWatchdogRef.current = setTimeout(() => {
            if (thisSpeechId === currentSpeechIdRef.current && isAudioLoadingRef.current) {
              console.warn('[Trainova Audio] Audio loading timed out (7s). Triggering speech fallback.');
              triggerFallback('Audio loading timed out. Switched to voice fallback.');
            }
          }, 7000);

          let playbackInitiated = false;
          const startPlaybackWhenReady = async () => {
            if (playbackInitiated || isCleaningUpRef.current || thisSpeechId !== currentSpeechIdRef.current) return;
            playbackInitiated = true;
            if (audioLoadWatchdogRef.current) {
              clearTimeout(audioLoadWatchdogRef.current);
              audioLoadWatchdogRef.current = null;
            }

            // Required audio data and player are fully loaded and ready
            setIsAudioReady(true);

            // Resume audio context if browser suspended it
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
              try {
                await audioContextRef.current.resume();
              } catch {}
            }

            // Route AI audio through AudioContext:
            // 1. To mixedDestination (for session meeting recording)
            // 2. AND to destination (so user hears persona through hardware speakers)
            if (activeAiSourceRef.current) {
              try {
                activeAiSourceRef.current.disconnect();
              } catch {}
              activeAiSourceRef.current = null;
            }
            // Route AI audio through AudioContext:
            // 1. To mixedDestination (for persistent session meeting recording)
            // 2. AND to destination (so user hears persona through hardware speakers)
            if (audioContextRef.current) {
              const ctx = audioContextRef.current;
              if (ctx.state === 'suspended') {
                ctx.resume().catch(() => {});
              }
              try {
                if (!(audio as any).__hasMediaElementSource) {
                  const aiSource = ctx.createMediaElementSource(audio);
                  (audio as any).__hasMediaElementSource = true;
                  activeAiSourceRef.current = aiSource;
                  if (mixedDestinationRef.current) {
                    aiSource.connect(mixedDestinationRef.current);
                  }
                  aiSource.connect(ctx.destination);
                }
              } catch (nodeErr) {
                // If createMediaElementSource throws, native HTMLAudioElement still routes to default speakers
                console.debug('[Trainova Audio] MediaElementSource routing note:', nodeErr);
              }
            }

            try {
              const playPromise = audio.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    setIsAutoplayBlocked(false);
                    pendingAutoplayRef.current = null;
                  })
                  .catch((playErr) => {
                    if (thisSpeechId !== currentSpeechIdRef.current) return;
                    setIsAudioLoading(false);
                    isAudioLoadingRef.current = false;
                    isEchoCooldownRef.current = false;
                    unmuteMicAfterAiSpeech();
                    if (playErr?.name === 'AbortError') {
                      setIsAiSpeaking(false);
                      isAiSpeakingRef.current = false;
                      setInterimTranscript('');
                      return;
                    }
                    const isAutoplayErr =
                      playErr?.name === 'NotAllowedError' ||
                      /autoplay|interact|notallowed|not allowed|gesture|denied|user gesture/i.test(
                        `${playErr?.name || ''} ${playErr?.message || ''}`
                      );
                    if (isAutoplayErr) {
                      console.warn(
                        '[Trainova Audio] Audio playback rejected due to browser autoplay policy. Showing user interaction prompt.',
                        playErr?.message || playErr
                      );
                      setIsAutoplayBlocked(true);
                      pendingAutoplayRef.current = async () => {
                        try {
                          setIsAiSpeaking(true);
                          isAiSpeakingRef.current = true;
                          audio.currentTime = 0;
                          await audio.play();
                          setIsAutoplayBlocked(false);
                        } catch (resumeErr) {
                          console.warn('[Trainova Audio] Resumed playback failed:', resumeErr);
                          triggerFallback('Resumed playback restricted. Using voice fallback.');
                        }
                      };
                      return;
                    }
                    console.warn('[Trainova Audio] Audio playback rejected (likely mobile autoplay):', playErr?.message || playErr);
                    triggerFallback('Audio playback restricted by browser. Using voice fallback.');
                  });
              }
            } catch {
              if (thisSpeechId === currentSpeechIdRef.current) {
                setIsAudioLoading(false);
                isAudioLoadingRef.current = false;
                triggerFallback('Playback error encountered. Using voice fallback.');
              }
            }
          };

          const handlePlaying = () => {
            if (isCleaningUpRef.current || thisSpeechId !== currentSpeechIdRef.current) {
              try {
                audio.pause();
                audio.src = '';
                audio.load();
              } catch {}
              if (activeAudioRef.current === audio) {
                activeAudioRef.current = null;
              }
              return;
            }
            setIsAudioLoading(false);
            isAudioLoadingRef.current = false;
            setAudioLoadingError(null);
            setInterimTranscript('');
            setIsAiSpeaking(true);
            isAiSpeakingRef.current = true;
            isEchoCooldownRef.current = true;
            abortRecognitionSafely();
            muteMicForAiSpeech();
          };

          const handleEnded = () => {
            if (thisSpeechId !== currentSpeechIdRef.current) return;
            // If audio completed suspiciously fast (<0.4s) while text is a multi-word sentence, it was likely an empty/silent or mock buffer
            if (typeof audio.duration === 'number' && audio.duration > 0 && audio.duration < 0.4 && text && text.trim().length > 15) {
              triggerFallback('Audio completed prematurely. Using voice fallback.');
              return;
            }
            setIsAudioLoading(false);
            isAudioLoadingRef.current = false;
            concludeAiSpeech(onEnd);
          };

          const handleError = () => {
            if (thisSpeechId !== currentSpeechIdRef.current) return;
            console.warn('[Trainova Audio] Audio load error:', audio.error?.message || 'Media load error');
            triggerFallback('Audio loading failed. Using voice fallback.');
          };

          // Strict gate: only handlePlaying on 'playing', never on premature 'play'
          audio.onplaying = handlePlaying;
          audio.onended = handleEnded;
          audio.onerror = handleError;

          const onMediaReady = () => {
            if (thisSpeechId !== currentSpeechIdRef.current) return;
            void startPlaybackWhenReady();
          };

          audio.addEventListener('canplay', onMediaReady, { once: true });
          audio.addEventListener('canplaythrough', onMediaReady, { once: true });
          audio.addEventListener('seeked', onMediaReady, { once: true });

          activeAudioCleanupRef.current = () => {
            audio.removeEventListener('canplay', onMediaReady);
            audio.removeEventListener('canplaythrough', onMediaReady);
            audio.removeEventListener('seeked', onMediaReady);
            audio.onplaying = null;
            audio.onended = null;
            audio.onerror = null;
          };

          // If not reusing preloaded audio, set src to begin buffering & decoding
          if (!isReusingPreloaded) {
            audio.src = audioUrl;
          } else {
            try {
              audio.currentTime = 0;
            } catch {}
          }

          // Media readiness: if reusing preloaded audio, or if media already has sufficient data and not seeking, play immediately!
          if (isReusingPreloaded) {
            if (audio.seeking) {
              audio.addEventListener('seeked', onMediaReady, { once: true });
            } else {
              void startPlaybackWhenReady();
            }
          } else if (audio.readyState >= 2) {
            void startPlaybackWhenReady();
          }
          return;
        } catch {
          if (thisSpeechId === currentSpeechIdRef.current) {
            triggerFallback('Audio initialization error. Using voice fallback.');
          }
          return;
        }
      }

      // If no audioUrl or creation failed, use SpeechSynthesis
      if (text) {
        speakSynthesis(text, onEnd, vConfig, false, true);
      } else {
        setIsAudioLoading(false);
        isAudioLoadingRef.current = false;
        concludeAiSpeech(onEnd);
      }
    },
    [personaVoiceConfig, stopSpeaking, abortRecognitionSafely, speakSynthesis, concludeAiSpeech, unmuteMicAfterAiSpeech]
  );

  // 5. Hardware Microphone & Speech Recognition Core Lifecycle
  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return;

    let isSubscribed = true;
    isCleaningUpRef.current = false;

    async function initHardwareAndSTT() {
      try {
        // Web Speech Recognition Initialization Factory
        const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

        // Web Speech Recognition Initialization Factory
        const createRecognitionInstance = () => {
          if (!SpeechRec || isCleaningUpRef.current) return null;
          try {
            const rec = new SpeechRec();
            rec.continuous = true;
            rec.interimResults = true;
            rec.lang = language.toLowerCase().startsWith('hi') ? 'hi-IN' : language;
            rec.maxAlternatives = 1;

            rec.onstart = () => {
              speechErrorCountRef.current = 0;
              lastSpeechEventTimestampRef.current = Date.now();
              if (isSubscribed && !isCleaningUpRef.current) {
                isRecognizingRef.current = true;
                setIsListening(true);
              }
            };

            rec.onspeechstart = () => {
              lastSpeechEventTimestampRef.current = Date.now();
            };

            rec.onend = () => {
              isRecognizingRef.current = false;
              if (recognitionRef.current === rec) {
                recognitionRef.current = null;
              }

              if (!isSubscribed || isCleaningUpRef.current) return;

              // 1. Commit any unfinalized interim speech so speech chunks are NEVER dropped!
              const pendingInterim = currentInterimTextRef.current.trim();
              if (pendingInterim) {
                accumulatedFinalTextRef.current = mergeTranscriptStrings(
                  accumulatedFinalTextRef.current,
                  pendingInterim
                );
                currentInterimTextRef.current = '';
                setInterimTranscript(accumulatedFinalTextRef.current);
              }

              const hasRecordedSpeech = Boolean(accumulatedFinalTextRef.current.trim());

              if (isMobileBrowser) {
                setAudioLevel(0);
              }

              // If muted, AI speaking, audio loading, echo cooldown, or blocked, stop here
              if (
                isMutedRef.current ||
                isAiSpeakingRef.current ||
                isAudioLoadingRef.current ||
                isEchoCooldownRef.current ||
                speechServiceBlockedRef.current
              ) {
                setIsListening(false);
                return;
              }

              if (hasRecordedSpeech) {
                // The learner spoke! On mobile, onend is the natural pause/end-of-phrase.
                consecutiveIdleEndsRef.current = 0;

                // When speech recognition confirms silence via onend, start the 5-second auto-submit countdown
                if (silenceTimerRef.current) {
                  clearTimeout(silenceTimerRef.current);
                  silenceTimerRef.current = null;
                }
                if (!countdownIntervalRef.current) {
                  startSilenceCountdown();
                }

                // Restart recognition safely after calm backoff (750ms mobile, 300ms desktop)
                // in case the learner paused and wants to say more
                const restartBackoff = isMobileBrowser ? 750 : 300;
                setTimeout(() => {
                  if (
                    !isCleaningUpRef.current &&
                    !isMutedRef.current &&
                    enabledRef.current &&
                    !isAiSpeakingRef.current &&
                    !isAudioLoadingRef.current &&
                    !isEchoCooldownRef.current &&
                    !speechServiceBlockedRef.current
                  ) {
                    startRecognitionSafely(true);
                  }
                }, restartBackoff);
              } else {
                // No speech recorded yet (idle pause / silence boundary while learner thinks)
                consecutiveIdleEndsRef.current += 1;

                // Keep isListening true for the learner so the microphone remains ready and active.
                // Restart recognition promptly so the learner's first spoken words are immediately captured without delay.
                const idleRestartDelay = isMobileBrowser ? 450 : 300;
                setTimeout(() => {
                  if (
                    !isCleaningUpRef.current &&
                    !isMutedRef.current &&
                    enabledRef.current &&
                    !isAiSpeakingRef.current &&
                    !isAudioLoadingRef.current &&
                    !isEchoCooldownRef.current &&
                    !speechServiceBlockedRef.current
                  ) {
                    startRecognitionSafely(true);
                  }
                }, idleRestartDelay);
              }
            };

            rec.onerror = (e: any) => {
              const errCode = e.error || '';
              if (errCode === 'not-allowed' || errCode === 'service-not-allowed') {
                const hasHardwareAudio = Boolean(
                  (mediaStreamRef.current &&
                    mediaStreamRef.current.getAudioTracks().length > 0 &&
                    mediaStreamRef.current.getAudioTracks().some((t) => t.readyState === 'live')) ||
                  micStreamStateRef.current === 'granted'
                );

                console.warn(
                  `[Trainova Audio] Speech recognition service returned '${errCode}'. Hardware audio status: ${hasHardwareAudio ? 'live' : 'not ready'}.`
                );

                if (!hasHardwareAudio && !isMobileBrowser) {
                  speechServiceBlockedRef.current = true;
                }

                // Per GEMINI.md: Never set micPermissionDenied to true if Web Speech API returns service-not-allowed or not-allowed.
                // Cloud speech recognition failures must not trigger the blocking red hardware-mic banner.
                // Hardware getUserMedia rejection (NotAllowedError) is the sole trigger for micPermissionDenied.
                isRecognizingRef.current = false;
                setIsListening(false);

                // Seamless Server-Side STT Fallback on mobile or when cloud speech service is unavailable
                if (errCode === 'service-not-allowed' || isMobileBrowser) {
                  activateServerSttFallback(`Speech recognition service returned '${errCode}'`);
                  return;
                }

                // If hardware mic is live, retry after a calm backoff rather than permanently locking out
                setTimeout(() => {
                  if (
                    !isCleaningUpRef.current &&
                    !isMutedRef.current &&
                    enabledRef.current &&
                    !isAiSpeakingRef.current &&
                    !isAudioLoadingRef.current &&
                    !isEchoCooldownRef.current
                  ) {
                    speechServiceBlockedRef.current = false;
                    startRecognitionSafely();
                  }
                }, 3000);
              } else if (errCode === 'audio-capture') {
                console.warn('[Trainova AudioDiag:STT] audio-capture warning: mic resource busy. Discarding competing recorder and retrying STT calmly.');
                // Discard any competing MediaRecorder that might be holding the microphone hardware!
                discardLearnerRecorder();
                mobileSttOwnsMicRef.current = true;
                isRecognizingRef.current = false;
                unmuteMicAfterAiSpeech();
                // Gently restart STT after brief backoff
                setTimeout(() => {
                  if (
                    !isCleaningUpRef.current &&
                    !isMutedRef.current &&
                    enabledRef.current &&
                    !isAiSpeakingRef.current &&
                    !isAudioLoadingRef.current &&
                    !speechServiceBlockedRef.current
                  ) {
                    startRecognitionSafely(true);
                  }
                }, 800);
              } else if (errCode === 'network') {
                speechErrorCountRef.current += 1;
                console.warn(`[Trainova Audio] Web Speech service network blip (count: ${speechErrorCountRef.current}). Backing off before reconnect.`);
                isRecognizingRef.current = false;
                const backoffMs = Math.min(4000, 1000 * Math.pow(1.5, speechErrorCountRef.current - 1));
                setTimeout(() => {
                  if (
                    !isCleaningUpRef.current &&
                    !isMutedRef.current &&
                    enabledRef.current &&
                    !isAiSpeakingRef.current &&
                    !isAudioLoadingRef.current &&
                    !isEchoCooldownRef.current
                  ) {
                    startRecognitionSafely();
                  }
                }, backoffMs);
              } else if (errCode === 'no-speech' || errCode === 'aborted') {
                // Non-fatal: normal pause or silence boundary
                isRecognizingRef.current = false;
              } else {
                console.debug('[Trainova Audio] SpeechRec status:', errCode);
                isRecognizingRef.current = false;
              }
            };

            rec.onresult = (event: any) => {
              lastSpeechEventTimestampRef.current = Date.now();
              const isSynthSpeaking = Boolean(
                activeSpeechCancelRef.current !== null &&
                typeof window !== 'undefined' &&
                window.speechSynthesis &&
                (window.speechSynthesis.speaking || window.speechSynthesis.pending)
              );
              const isAudioElementPlaying = Boolean(activeAudioRef.current && !activeAudioRef.current.paused);
              const isPlaybackActive =
                isCleaningUpRef.current ||
                isMutedRef.current ||
                isAiSpeakingRef.current ||
                isAudioLoadingRef.current ||
                isEchoCooldownRef.current ||
                (isSynthSpeaking && isAiSpeakingRef.current) ||
                isAudioElementPlaying;

              if (isPlaybackActive) {
                console.warn('[Trainova AudioDiag:STT] Voice result discarded by active playback guard:', {
                  isAiSpeaking: isAiSpeakingRef.current,
                  isAudioLoading: isAudioLoadingRef.current,
                  isEchoCooldown: isEchoCooldownRef.current,
                  isSynthSpeaking,
                  isAudioElementPlaying,
                });
                setInterimTranscript('');
                turnLeadingHesitationRef.current = '';
                accumulatedFinalTextRef.current = '';
                currentInterimTextRef.current = '';
                return;
              }

              // Active speech detected — reset idle counter
              consecutiveIdleEndsRef.current = 0;

              // Anchor the measured speech window for this learner turn (first result = onset,
              // last result = offset) so duration and hesitation are real measurements
              if (!turnSpeechStartRef.current) {
                turnSpeechStartRef.current = Date.now();
              }
              lastSpeechResultAtRef.current = Date.now();

              let finalChunk = '';
              let interimChunk = '';
              let finalConfidence = 0;

              for (let i = event.resultIndex; i < event.results.length; ++i) {
                const transcriptText = event.results[i][0]?.transcript || '';
                if (event.results[i].isFinal) {
                  const conf = event.results[i][0]?.confidence;
                  if (typeof conf === 'number' && conf > 0) {
                    finalConfidence = Math.max(finalConfidence, conf);
                  }
                  finalChunk += transcriptText;
                } else {
                  interimChunk += transcriptText;
                }
              }

              if (finalConfidence > 0) {
                sttConfidenceRef.current = finalConfidence;
              }

              let updated = false;

              // Check if learner opened their speech with hesitation word(s) e.g. "umm", "um", "hmm", "uh", "uhh"
              if (!accumulatedFinalTextRef.current) {
                const rawLeadingCandidate = (interimChunk.trim() || finalChunk.trim());
                if (rawLeadingCandidate) {
                  const leadMatch = rawLeadingCandidate.match(LEADING_HESITATION_REGEX);
                  if (leadMatch && leadMatch[0]) {
                    turnLeadingHesitationRef.current = leadMatch[0].trim();
                  }
                }
              }

              if (finalChunk.trim()) {
                let chunkToMerge = finalChunk.trim();
                // If learner opened with a hesitation word and the speech recognizer dropped it in final chunk,
                // restore the leading hesitation word so it is permanently preserved in the transcript
                if (
                  turnLeadingHesitationRef.current &&
                  !accumulatedFinalTextRef.current &&
                  !LEADING_HESITATION_REGEX.test(chunkToMerge)
                ) {
                  chunkToMerge = `${turnLeadingHesitationRef.current} ${chunkToMerge}`;
                }
                accumulatedFinalTextRef.current = mergeTranscriptStrings(
                  accumulatedFinalTextRef.current,
                  chunkToMerge
                );
                currentInterimTextRef.current = '';
                updated = true;
              }

              if (interimChunk.trim()) {
                let interimToSet = interimChunk.trim();
                // If learner opened with a hesitation word and the speech recognizer dropped it in subsequent interim results,
                // restore the leading hesitation word in the interim hypothesis
                if (
                  turnLeadingHesitationRef.current &&
                  !accumulatedFinalTextRef.current &&
                  !LEADING_HESITATION_REGEX.test(interimToSet)
                ) {
                  interimToSet = `${turnLeadingHesitationRef.current} ${interimToSet}`;
                }
                currentInterimTextRef.current = interimToSet;
                updated = true;
              }

              if (updated) {
                if (isMobileBrowser) {
                  const pulse = Math.min(85, Math.max(35, Math.floor(Math.random() * 40) + 40));
                  setAudioLevel(pulse);
                }

                const currentAcc = accumulatedFinalTextRef.current.trim();
                const interim = currentInterimTextRef.current.trim();
                let fullHypothesis = currentAcc;
                if (interim) {
                  fullHypothesis = mergeTranscriptStrings(currentAcc, interim);
                }

                setInterimTranscript(fullHypothesis);

                // If the learner starts speaking again, cancel/reset the countdown and continue capturing speech
                cancelSilenceCountdown();

                // Calibrated silence timer:
                // Optimized to 600ms (desktop) / 750ms (mobile) for prompt auto-submit countdown initiation
                // (with 1400ms desktop / 1600ms mobile reference bounds preserved).
                const silenceLimit = isMobileBrowser ? 750 : 600;

                silenceTimerRef.current = setTimeout(() => {
                  startSilenceCountdown();
                }, silenceLimit);
              }
            };

            return rec;
          } catch (err: any) {
            console.warn('[Trainova Audio] SpeechRec factory error:', err?.message || err);
            return null;
          }
        };

        createRecognitionRef.current = createRecognitionInstance;

        if (SpeechRec) {
          const rec = createRecognitionInstance();
          if (rec) {
            recognitionRef.current = rec;
            // Do NOT start recognition on mount! Recognition starts ONLY after the opening AI turn finishes.
          }
        } else {
          // If browser/PWA does not support Web Speech API (e.g. older iOS WebViews or standalone PWAs),
          // activate Server-Side STT Fallback automatically so learner audio is reliably captured.
          activateServerSttFallback('Web Speech API is not supported in this browser/PWA environment');
        }

        // Fulfill any early recognition start request queued during mount
        if (pendingStartRecognitionRef.current && !isCleaningUpRef.current && !isMutedRef.current && enabledRef.current) {
          pendingStartRecognitionRef.current = false;
          startRecognitionSafely(true);
        }

        // Acquire hardware audio stream for live analysis, level meters, and recording
        if (isMobileBrowser) {
          // On mobile devices, Google Speech Recognition (webkitSpeechRecognition) needs exclusive
          // access to Android's AudioRecord HAL. Running a background getUserMedia stream locks the
          // hardware and triggers "Speech Recognition and Synthesis from Google cannot record now as Chrome is recording".
          mobileSttOwnsMicRef.current = true;
          micStreamStateRef.current = 'granted';
          setMicReady(true);
          setMicPermissionDenied(false);
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
          }
          if (pendingStartRecognitionRef.current && !isCleaningUpRef.current && !isMutedRef.current && enabledRef.current) {
            pendingStartRecognitionRef.current = false;
            startRecognitionSafely(true);
          }
        } else if (typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') {
          try {
            const stream = await acquireMicrophoneStream();

            if (!isSubscribed || isCleaningUpRef.current) {
              stream.getTracks().forEach((t) => t.stop());
              return;
            }

            setMicPermissionDenied(false);

            // Attach hardware microphone stream for live audio analysis, visualization,
            // and learner audio recording. If specific hardware contention occurs on STT start,
            // rec.onerror gracefully releases tracks via audio-capture handler.
            attachMicrophoneStream(stream);
            micStreamStateRef.current = 'granted';
            setMicReady(true);

            // If a start request arrived while awaiting microphone stream, fulfill it now
            if (pendingStartRecognitionRef.current && !isCleaningUpRef.current && !isMutedRef.current && enabledRef.current) {
              pendingStartRecognitionRef.current = false;
              startRecognitionSafely(true);
            }
          } catch (micErr: any) {
            console.warn('[Trainova Audio] Microphone stream warning:', micErr?.message || micErr);
            if (micErr?.name === 'NotAllowedError' || micErr?.name === 'PermissionDeniedError') {
              setMicPermissionDenied(true);
              micStreamStateRef.current = 'denied';
              setMicReady(false);
            }
          }
        }

        // Opening Turn Fail-Safe: If AI speech is still marked as speaking after timeout
        // (e.g. mobile autoplay blocked audio.play(), or speech synthesis didn't start),
        // automatically release mic lock so learner is not stranded!
        openingTimerRef.current = setTimeout(() => {
          const isSynthSpeaking = typeof window !== 'undefined' && 'speechSynthesis' in window &&
            (window.speechSynthesis.speaking || window.speechSynthesis.pending);
          if (
            !isCleaningUpRef.current &&
            isAiSpeakingRef.current &&
            (!activeAudioRef.current || activeAudioRef.current.paused) &&
            !isSynthSpeaking
          ) {
            console.warn('[Trainova Audio] Opening statement fail-safe triggered: auto-enabling learner mic.');
            concludeAiSpeech();
          }
        }, isMobileBrowser ? 4500 : 7000);
      } catch (err: any) {
        console.warn('Audio subsystem initialization warning:', err.message);
        if (onErrorRef.current) onErrorRef.current(err);
      }
    }

    initHardwareAndSTT();

    // 6. Active Watchdog Timer: keeps STT listening alive indefinitely without fighting onend
    watchdogTimerRef.current = setInterval(() => {
      const isSpeakingActive =
        isAiSpeakingRef.current ||
        isAudioLoadingRef.current ||
        isEchoCooldownRef.current ||
        (activeSpeechCancelRef.current !== null && typeof window !== 'undefined' && window.speechSynthesis?.speaking) ||
        (activeAudioRef.current && !activeAudioRef.current.paused);

      const now = Date.now();
      if (
        !isCleaningUpRef.current &&
        !isMutedRef.current &&
        enabledRef.current &&
        !isSpeakingActive &&
        !speechServiceBlockedRef.current
      ) {
        if (!isRecognizingRef.current) {
          startRecognitionSafely(true);
        } else if (now - lastSpeechEventTimestampRef.current > 45000) {
          // Chrome continuous speech recognition silently freezes after ~45s of stream time;
          // Refresh the instance proactively to ensure words keep being captured across long sessions.
          lastSpeechEventTimestampRef.current = now;
          startRecognitionSafely(true);
        }
      }
    }, 1500);

    return () => {
      isSubscribed = false;
      isCleaningUpRef.current = true;
      if (openingTimerRef.current) {
        clearTimeout(openingTimerRef.current);
        openingTimerRef.current = null;
      }
      if (watchdogTimerRef.current) {
        clearInterval(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      cancelSilenceCountdown();
      if (serverSttSilenceTimerRef.current) {
        clearTimeout(serverSttSilenceTimerRef.current);
        serverSttSilenceTimerRef.current = null;
      }
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      discardLearnerRecorder();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (micSourceNodeRef.current) {
        try {
          micSourceNodeRef.current.disconnect();
        } catch {}
        micSourceNodeRef.current = null;
      }
      if (activeAiSourceRef.current) {
        try {
          activeAiSourceRef.current.disconnect();
        } catch {}
        activeAiSourceRef.current = null;
      }
      if (audioContextRef.current) {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch(() => {});
        }
        audioContextRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {}
        recognitionRef.current = null;
      }
      if (activeAudioCleanupRef.current) {
        try {
          activeAudioCleanupRef.current();
        } catch {}
        activeAudioCleanupRef.current = null;
      }
      if (activeAudioRef.current) {
        try {
          activeAudioRef.current.onplaying = null;
          activeAudioRef.current.onplay = null;
          activeAudioRef.current.onended = null;
          activeAudioRef.current.onerror = null;
          activeAudioRef.current.pause();
          activeAudioRef.current.src = '';
          activeAudioRef.current.load();
        } catch {}
        activeAudioRef.current = null;
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      isEchoCooldownRef.current = false;
      createRecognitionRef.current = null;
      pendingStartRecognitionRef.current = false;
    };
  }, [language, enabled]);

  // 7. Mute / Unmute Toggle
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      isMutedRef.current = next;

      if (next) {
        // Muting: stop active recognition
        stopRecognitionSafely();
      } else {
        // USER REQUIREMENT: If I unmute the mic... TTS stops speaking immediately!
        // Immediately halt any active AI speech / TTS so the learner can speak uninterrupted.
        speechServiceBlockedRef.current = false;
        speechErrorCountRef.current = 0;
        consecutiveIdleEndsRef.current = 0;
        isEchoCooldownRef.current = false;
        setMicPermissionDenied(false);
        stopSpeaking();
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().catch(() => {});
        }
        startRecognitionSafely(true);
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach((t) => {
          t.enabled = !next;
        });
      }
      return next;
    });
  }, [startRecognitionSafely, stopRecognitionSafely, stopSpeaking]);

  // 8. Explicit Stop All (for simulation conclusion / exit)
  const stopAll = useCallback(() => {
    isCleaningUpRef.current = true;
    if (watchdogTimerRef.current) {
      clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    abortRecognitionSafely();
    stopSpeaking();
    discardLearnerRecorder();
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (micSourceNodeRef.current) {
      try {
        micSourceNodeRef.current.disconnect();
      } catch {}
      micSourceNodeRef.current = null;
    }
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    micGateRef.current = null;
    mixedDestinationRef.current = null;
    setMixedAudioStream(null);
    setIsListening(false);
  }, [abortRecognitionSafely, stopSpeaking, discardLearnerRecorder]);

  const stopListening = useCallback(() => {
    abortRecognitionSafely();
  }, [abortRecognitionSafely]);

  const startListening = useCallback(async () => {
    speechServiceBlockedRef.current = false;
    speechErrorCountRef.current = 0;
    consecutiveIdleEndsRef.current = 0;
    isEchoCooldownRef.current = false;
    setMicPermissionDenied(false);

    // Unconditionally enable all hardware microphone tracks immediately
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = true;
      });
    }

    // If audio loading was stuck or unhandled, clear it so learner STT is never blocked
    if (isAudioLoadingRef.current) {
      setIsAudioLoading(false);
      isAudioLoadingRef.current = false;
    }

    // Clear any pending synthesis state so learner STT is ready
    activeSpeechCancelRef.current = null;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }

    // The learner asking to speak is the strongest possible permission signal:
    // re-acquire the microphone if the mount-time attempt was denied or never ran.
    if (!mediaStreamRef.current && !mobileSttOwnsMicRef.current && !isMobileBrowser) {
      await ensureMicrophone();
    }

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume().catch(() => {});
    }

    setIsMuted(false);
    isMutedRef.current = false;

    // Ensure tracks are enabled after possible re-acquisition
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = true;
      });
    }

    // Force release any stuck AI speaking lock if learner actively triggers listening
    if (isAiSpeakingRef.current) {
      stopSpeaking();
    }

    listeningWindowOpenRef.current = Date.now();
    startRecognitionSafely(true);
  }, [ensureMicrophone, startRecognitionSafely, stopSpeaking]);

  const retryMicAccess = useCallback(async () => {
    speechServiceBlockedRef.current = false;
    speechErrorCountRef.current = 0;
    consecutiveIdleEndsRef.current = 0;
    isEchoCooldownRef.current = false;
    if (isMobileBrowser) {
      mobileSttOwnsMicRef.current = true;
      setMicPermissionDenied(false);
      micStreamStateRef.current = 'granted';
      setMicReady(true);
      setIsMuted(false);
      isMutedRef.current = false;
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      startRecognitionSafely(true);
      return true;
    }
    try {
      const stream = await acquireMicrophoneStream();
      setMicPermissionDenied(false);

      attachMicrophoneStream(stream);

      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume().catch(() => {});
      }

      micStreamStateRef.current = 'granted';
      setMicReady(true);
      setIsMuted(false);
      isMutedRef.current = false;
      startRecognitionSafely(true);
      return true;
    } catch (err: any) {
      console.warn('[Trainova Audio] Retry mic access failed:', err?.message || err);
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        setMicPermissionDenied(true);
        micStreamStateRef.current = 'denied';
        setMicReady(false);
      }
      return false;
    }
  }, [attachMicrophoneStream, startRecognitionSafely]);

  // Listen for browser permission state changes (e.g. user toggles microphone to Allow in URL bar)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;
    let isCancelled = false;

    try {
      navigator.permissions.query({ name: 'microphone' as any }).then((permStatus) => {
        if (isCancelled) return;
        if (permStatus.state === 'denied') {
          setMicPermissionDenied(true);
        } else if (permStatus.state === 'granted' || permStatus.state === 'prompt') {
          setMicPermissionDenied(false);
        }

        permStatus.onchange = () => {
          if (isCancelled) return;
          if (permStatus.state === 'granted') {
            setMicPermissionDenied(false);
            retryMicAccess();
          } else if (permStatus.state === 'denied') {
            setMicPermissionDenied(true);
          } else if (permStatus.state === 'prompt') {
            setMicPermissionDenied(false);
          }
        };
      }).catch(() => {
        // Permissions query for 'microphone' is not supported in all browsers
      });
    } catch {}

    return () => {
      isCancelled = true;
    };
  }, [retryMicAccess]);

  const resumeAutoplay = useCallback(async () => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
      } catch (e) {
        console.warn('[Trainova Audio] AudioContext resume note:', e);
      }
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.paused) {
      try {
        window.speechSynthesis.resume();
      } catch {}
    }
    setIsAutoplayBlocked(false);
    if (pendingAutoplayRef.current) {
      const pendingFn = pendingAutoplayRef.current;
      pendingAutoplayRef.current = null;
      try {
        await pendingFn();
      } catch (err) {
        console.warn('[Trainova Audio] Resumed playback error:', err);
      }
    }
  }, []);

  return {
    isMuted,
    toggleMute,
    isAiSpeaking,
    isAudioLoading,
    isAudioReady,
    isAutoplayBlocked,
    resumeAutoplay,
    audioLoadingError,
    isListening,
    interimTranscript,
    audioLevel,
    micPermissionDenied,
    micReady,
    retryMicAccess,
    ensureMicrophone,
    mediaStream: mediaStreamRef.current,
    mixedAudioStream: mixedAudioStream || mixedDestinationRef.current?.stream || mediaStreamRef.current,
    silenceCountdownSeconds,
    flushAndFinalize,
    preloadAndDecodeAudio,
    speakText,
    stopSpeaking,
    bargeIn,
    interruptionCount,
    interruptionEvents: interruptionEventsRef.current,
    stopAll,
    stopListening,
    isServerSttFallbackActive,
    activateServerSttFallback,
    startListening,
  };
}
